import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { Sparkles, Shield, ShieldCheck, Stethoscope, Tags, Handshake, ArrowRight } from "lucide-react";
import GoldServiceIcon from "./GoldServiceIcon";

/**
 * OwnerJourney — visual "all-in-one" strip placed on the homepage that shows
 * the core platform actions a vehicle owner / buyer needs, with a clear
 * numbered sequence (re-ordered so "List your vehicle" is the very first
 * step — that's the primary action CARTAL exists for):
 *   1. اعرض سيارتك  (List your vehicle)
 *   2. أمّن مركبتك  (Insurance)
 *   3. افحص قبل الشراء  (Pre-purchase inspection — for buyers)
 *   4. الفحص الفني الدوري  (Periodic inspection — for sellers)
 *   5. انقل الملكية  (Ownership transfer via Absher)
 *
 * Each step is a clickable tile with a distinct icon + gold accent.
 */
const STEPS = [
  {
    n: 1,
    to: "/sell",
    icon: Tags,
    ar: { title: "اعرض سيارتك", desc: "أضف الإعلان على كارتال مجاناً وتواصل مباشر مع المشترين." },
    en: { title: "List your vehicle", desc: "Post your listing on CARTAL free and chat with buyers directly." },
    color: "#8a6f2e",
    badge: { ar: "ابدأ هنا", en: "Start here" },
    testid: "journey-list",
  },
  {
    n: 2,
    to: "/insurance",
    icon: Shield,
    ar: { title: "أمّن مركبتك", desc: "تصفّح أبرز شركات التأمين السعودية المعتمدة." },
    en: { title: "Insure your vehicle", desc: "Browse leading accredited Saudi insurers." },
    color: "#1f4f8a",
    testid: "journey-insure",
  },
  {
    n: 3,
    to: "/pre-purchase-inspection",
    icon: Stethoscope,
    ar: {
      audience: "للمشتري",
      title: "افحص قبل الشراء",
      desc: "شيك أب اختياري قبل دفع الثمن — إحكام، كيو ستوب، بترومين.",
    },
    en: {
      audience: "For buyers",
      title: "Pre-purchase check-up",
      desc: "Optional inspection before you pay — Ehkam, Q-Stop, Petromin.",
    },
    color: "#1f4f8a",
    testid: "journey-prepurchase",
  },
  {
    n: 4,
    to: "/periodic-inspection",
    icon: ShieldCheck,
    ar: {
      audience: "للبائع",
      title: "الفحص الفني الدوري",
      desc: "إلزامي للمرور — سلامة، فحص، المروج.",
    },
    en: {
      audience: "For sellers",
      title: "Periodic technical inspection",
      desc: "Mandatory by traffic law — Salama, Fahas, Al-Morooj.",
    },
    color: "#1f6f3a",
    badge: { ar: "إلزامي", en: "Mandatory" },
    testid: "journey-periodic",
  },
  {
    n: 5,
    to: "/ownership-transfer",
    icon: Handshake,
    ar: { title: "انقل الملكية", desc: "روابط مباشرة لأبشر والمرور لإتمام نقل الملكية." },
    en: { title: "Transfer ownership", desc: "Direct links to Absher & Traffic for ownership transfer." },
    color: "#0a0a0a",
    testid: "journey-deal",
  },
];

export default function OwnerJourney() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  return (
    <section
      className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-14 md:py-20"
      data-testid="owner-journey"
      aria-label={isAr ? "رحلة كاملة على كارتال" : "Complete CARTAL journey"}
    >
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8 md:mb-10">
        <div className="max-w-2xl">
          <div className="overline overline-gold mb-3 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> {isAr ? "كل ما تحتاجه — مرتّب في مكان واحد" : "Everything you need — organised in one place"}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05] font-medium text-[#0a0a0a]">
            {isAr
              ? "أمّن. افحص للشراء. افحص للنقل. اعرض. انقل."
              : "Insure. Check-up. Periodic. List. Transfer."}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#525252] leading-relaxed">
            {isAr
              ? "كارتال يجمع لك الجهات المعتمدة لكل خطوة في رحلتك. تنقّر، تذهب لموقع المزوّد، تنجز — بدلاً من البحث في مواقع متعددة."
              : "CARTAL gathers the accredited providers you need at each step. Click, jump to the provider's site, get it done — instead of hunting across multiple websites."}
          </p>
        </div>
      </div>

      <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
        {STEPS.map((s) => {
          const Icon = s.icon;
          const t = isAr ? s.ar : s.en;
          return (
            <li key={s.n} className="relative">
              <Link
                to={s.to}
                data-testid={s.testid}
                className="group flex flex-col h-full rounded-2xl border-2 border-[#0a0a0a]/8 bg-white p-5 hover:border-[#c9a55c] hover:shadow-lg transition-all"
              >
                {/* Step number ribbon */}
                <div className="flex items-start justify-between mb-4">
                  <span
                    className="inline-flex items-center justify-center w-12 h-12 rounded-2xl shrink-0"
                    style={{
                      backgroundColor: `${s.color}12`,
                      border: `1.5px solid ${s.color}40`,
                    }}
                    aria-hidden
                  >
                    <Icon className="w-6 h-6" strokeWidth={2} style={{ color: s.color }} />
                  </span>
                  <span
                    className="text-[10px] uppercase tracking-[0.22em] font-bold"
                    style={{ color: s.color }}
                  >
                    {isAr ? "خطوة" : "Step"} {s.n}
                  </span>
                </div>

                {/* Audience tag (For buyers / For sellers) — only on inspection cards */}
                {t.audience && (
                  <span
                    className="self-start inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1.5"
                    style={{ backgroundColor: `${s.color}18`, color: s.color }}
                  >
                    {t.audience}
                  </span>
                )}

                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h3 className="font-display text-base sm:text-lg font-semibold text-[#0a0a0a] leading-tight">
                    {t.title}
                  </h3>
                  {s.badge && (
                    <span
                      className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold"
                      style={{ backgroundColor: "#dc2626", color: "#ffffff" }}
                    >
                      {isAr ? s.badge.ar : s.badge.en}
                    </span>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-[#525252] leading-relaxed flex-1">{t.desc}</p>

                <span
                  className="mt-4 inline-flex items-center gap-1 text-xs sm:text-sm font-semibold transition-colors"
                  style={{ color: s.color }}
                >
                  {isAr ? "ابدأ" : "Start"}
                  <ArrowRight className="w-4 h-4 rtl:rotate-180 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
                </span>
              </Link>

              {/* Connector arrow (hidden on mobile + on the last item) */}
              {s.n < STEPS.length && (
                <span
                  className="hidden lg:block absolute top-1/2 -translate-y-1/2 -end-3 z-10 pointer-events-none"
                  aria-hidden
                >
                  <ArrowRight className="w-5 h-5 text-[#c9a55c] rtl:rotate-180" />
                </span>
              )}
            </li>
          );
        })}
      </ol>

      {/* iter251 → iter297 — Premium standalone entry to "بوابة مزودي
          الخدمات". Uses a filled-gold icon well (instead of the neutral
          outline that step-tiles use) so it visually reads as a
          dedicated portal, not just another "more" tile. Same rounded
          wrapper + padding keep it in the visual family, RTL and colour
          palette identical to CARTAL's existing hero cards. */}
      <ul className="mt-4 md:mt-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
        <li className="lg:col-span-2">
          <Link
            to="/other-services"
            data-testid="journey-other-services"
            className="group relative flex items-center gap-4 h-full rounded-2xl border-2 border-[#0a0a0a] bg-[#0a0a0a] p-5 hover:-translate-y-0.5 hover:shadow-xl transition-all overflow-hidden"
            aria-label={isAr ? "بوابة مزودي الخدمات" : "Service Providers Portal"}
          >
            {/* subtle gold sheen — pure decorative */}
            <span
              aria-hidden
              className="absolute -end-6 -top-8 w-32 h-32 rounded-full opacity-20 blur-2xl"
              style={{ background: "radial-gradient(circle,#e8c873,transparent)" }}
            />
            <span
              className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl shrink-0 shadow-md"
              style={{
                background: "linear-gradient(135deg,#e8c873,#c9a55c 55%,#8a6f2e)",
                color: "#0a0a0a",
                border: "1.5px solid #d4b56e",
              }}
              aria-hidden
            >
              <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.25} />
            </span>
            <div className="min-w-0 flex-1">
              <div
                className="text-sm font-bold tracking-widest uppercase mb-1"
                style={{ color: "#e8c873" }}
              >
                {isAr ? "منظومة كارتال" : "CARTAL Ecosystem"}
              </div>
              <h3
                className="font-display text-2xl sm:text-3xl font-semibold leading-tight"
                style={{ color: "#ffffff" }}
              >
                {isAr ? "بوابة مزودي الخدمات" : "Service Providers Portal"}
              </h3>
              <p className="text-base sm:text-lg mt-1 leading-relaxed" style={{ color: "#d4b56e" }}>
                {isAr
                  ? "خدمات المركبات من مزودين موثوقين"
                  : "Vehicle services from trusted providers"}
              </p>
            </div>
            <ArrowRight
              className="w-5 h-5 rtl:rotate-180 shrink-0 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1"
              style={{ color: "#e8c873" }}
            />
          </Link>
        </li>
      </ul>
    </section>
  );
}
